import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  Crown,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Users2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  type EmergencyContactDto,
  useCreateTeamContact,
  useDeleteContact,
  useTeamEmergencyContacts,
  useUpdateContact,
} from '../api/useEmergencyContacts.js';
import { type RosterMember, useRoster } from '../api/useRoster.js';
import {
  type CreateTeamInput,
  type TeamDto,
  type TeamMemberDto,
  useAddTeamMembers,
  useCreateTeam,
  useDeleteTeam,
  useRemoveTeamMember,
  useSetTeamMemberRole,
  useTeam,
  useTeamsList,
  useUpdateTeam,
} from '../api/useTeams.js';
import { EmptyState } from '../components/EmptyState.js';
import { RoleGate } from '../components/RoleGate.js';
import { cn } from '../lib/cn.js';
import { formatRelative } from '../lib/format.js';

// Teams page. Two modes:
//   • /teams           — list + create new
//   • /teams/:id       — detail, edit, member add/remove
// Admin-only for all mutations; monitors get a read-only view via RoleGate on the
// action buttons, not the page itself.

export default function Teams() {
  return (
    <div className="mx-auto max-w-6xl">
      <TeamsList />
    </div>
  );
}

export function TeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const team = useTeam(id);
  const roster = useRoster();
  const [isEditing, setIsEditing] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  if (team.isLoading) {
    return <div className="mx-auto max-w-6xl text-sm text-ink-faint">Loading team…</div>;
  }
  if (team.isError || !team.data) {
    return (
      <div className="mx-auto max-w-6xl">
        <EmptyState
          icon={AlertTriangle}
          title="Team not found"
          description={(team.error as Error)?.message ?? 'This team may have been deleted.'}
        />
        <div className="mt-4">
          <Link to="/teams" className="text-sm text-brand-purple hover:underline">
            ← Back to teams
          </Link>
        </div>
      </div>
    );
  }

  const { team: detail, members } = team.data;
  const existingIds = new Set(members.map((m) => m.user_id));
  const assignable = (roster.data?.members ?? []).filter((m) => !existingIds.has(m.user_id));

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to="/teams"
        className="mb-4 inline-flex items-center gap-2 text-sm text-ink-faint hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to teams
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <TeamDot colour={detail.colour} />
            <h1 className="font-heading text-2xl font-bold">{detail.name}</h1>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-ink-faint">
            {detail.discipline ? (
              <span className="capitalize">{detail.discipline}</span>
            ) : (
              <span>No discipline</span>
            )}
            <span>·</span>
            <span>
              {detail.member_count} {detail.member_count === 1 ? 'member' : 'members'}
            </span>
            <span>·</span>
            <span>/{detail.slug}</span>
          </div>
          {detail.description && (
            <p className="mt-2 max-w-2xl text-sm text-ink-faint">{detail.description}</p>
          )}
        </div>
        <RoleGate allow={['admin']} fallback={null}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
            <DeleteTeamButton team={detail} onDeleted={() => navigate('/teams')} />
          </div>
        </RoleGate>
      </div>

      <div className="mt-6 panel">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="text-sm font-semibold text-ink">Members</div>
          <RoleGate allow={['admin']} fallback={null}>
            <button
              type="button"
              onClick={() => setShowAssign(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-deep-purple"
            >
              <Plus className="h-4 w-4" />
              Add members
            </button>
          </RoleGate>
        </div>

        {members.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-ink-faint">
            No members yet. Add workers to this team.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-line">
            <tbody className="divide-y divide-line">
              {members.map((m) => (
                <MemberRow key={m.user_id} member={m} teamId={detail.id} canRemove />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TeamContactsSection teamId={detail.id} />

      {isEditing && (
        <TeamForm
          mode="edit"
          initial={detail}
          onClose={() => setIsEditing(false)}
          onSaved={() => setIsEditing(false)}
        />
      )}
      {showAssign && (
        <AssignMembersModal
          teamId={detail.id}
          candidates={assignable}
          onClose={() => setShowAssign(false)}
        />
      )}
    </div>
  );
}

// Team-scoped emergency contacts. Notified for any worker on this team.
// Supports escalation tiers via notify_after_seconds.
function TeamContactsSection({ teamId }: { teamId: string }) {
  const contacts = useTeamEmergencyContacts(teamId);
  const [showAdd, setShowAdd] = useState(false);
  const list = contacts.data?.contacts ?? [];

  return (
    <div className="mt-6 panel">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-ink">Emergency contacts for this team</div>
          <p className="text-xs text-ink-faint">
            Notified when any worker on this team triggers an alarm.
          </p>
        </div>
        <RoleGate allow={['admin']} fallback={null}>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-deep-purple"
          >
            <Plus className="h-4 w-4" /> Add contact
          </button>
        </RoleGate>
      </header>
      {list.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-ink-faint">
          No contacts configured for this team yet.
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((c) => (
            <TeamContactRow key={c.id} contact={c} />
          ))}
        </ul>
      )}
      {showAdd && <AddTeamContactModal teamId={teamId} onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function TeamContactRow({ contact }: { contact: EmergencyContactDto }) {
  const update = useUpdateContact();
  const del = useDeleteContact();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone_e164 ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [tierMin, setTierMin] = useState(Math.round(contact.notify_after_seconds / 60));

  if (editing) {
    return (
      <li className="bg-brand-purple/5 p-3">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await update.mutateAsync({
              id: contact.id,
              name: name !== contact.name ? name : undefined,
              phone_e164: (phone || null) !== contact.phone_e164 ? phone || null : undefined,
              email: (email || null) !== contact.email ? email || null : undefined,
              notify_after_seconds:
                tierMin * 60 !== contact.notify_after_seconds ? tierMin * 60 : undefined,
            });
            setEditing(false);
          }}
          className="flex flex-col gap-2"
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-line-strong px-2 py-1 text-xs"
            />
            <input
              type="number"
              min={0}
              value={tierMin}
              onChange={(e) => setTierMin(Math.max(0, Number(e.target.value) || 0))}
              className="rounded border border-line-strong px-2 py-1 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61…"
              className="rounded border border-line-strong px-2 py-1 font-mono text-xs"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@…"
              className="rounded border border-line-strong px-2 py-1 font-mono text-xs"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs text-ink-faint hover:underline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={update.isPending}
              className="rounded bg-brand-purple px-2 py-1 text-xs font-semibold text-white hover:bg-brand-deep-purple"
            >
              Save
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 px-4 py-2">
      <Phone className="h-4 w-4 text-ink-mute" />
      <div className="flex-1 text-sm">
        <div className="font-medium">
          {contact.name}
          {contact.role && <span className="ml-2 text-xs text-ink-faint">· {contact.role}</span>}
        </div>
        <div className="text-xs text-ink-faint">
          {contact.phone_e164 && <span className="font-mono">{contact.phone_e164}</span>}
          {contact.email && <span className="ml-2 font-mono">{contact.email}</span>}
          {contact.notify_after_seconds > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
              <Bell className="h-2.5 w-2.5" />
              tier {Math.round(contact.notify_after_seconds / 60)} min
            </span>
          )}
        </div>
      </div>
      <RoleGate allow={['admin']} fallback={null}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-ink-faint hover:text-ink"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (!confirm(`Delete contact "${contact.name}"?`)) return;
              del.mutate(contact.id);
            }}
            className="text-xs text-red-700 hover:text-red-900"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </RoleGate>
    </li>
  );
}

function AddTeamContactModal({ teamId, onClose }: { teamId: string; onClose: () => void }) {
  const create = useCreateTeamContact();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [tierMin, setTierMin] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || (!phone.trim() && !email.trim())) return;
    await create.mutateAsync({
      team_id: teamId,
      name: name.trim(),
      role: role.trim() || null,
      phone_e164: phone.trim() || null,
      email: email.trim() || null,
      notify_after_seconds: tierMin * 60,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg bg-white p-5 shadow-panel">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Add team contact</h2>
          <button type="button" onClick={onClose} className="text-ink-mute hover:text-ink-dim">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
            className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
          />
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Role (e.g. Team lead, On-call backup)"
            className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61…"
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm font-mono"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@…"
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm font-mono"
            />
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">
              Escalation tier — notify after N minutes (0 = immediate)
            </span>
            <input
              type="number"
              min={0}
              value={tierMin}
              onChange={(e) => setTierMin(Math.max(0, Number(e.target.value) || 0))}
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
            />
          </label>
          {create.isError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {(create.error as Error).message}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-sm text-ink-faint hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              Create contact
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function TeamsList() {
  const { data, isLoading, isError, error } = useTeamsList();
  const [isCreating, setIsCreating] = useState(false);
  const teams = data?.teams ?? [];

  return (
    <>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Field</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Teams
          </h1>
          <p className="text-sm text-ink-faint">
            Group workers by discipline to enable targeted broadcasts and reports.
          </p>
        </div>
        <RoleGate allow={['admin']} fallback={null}>
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center gap-2 rounded-md bg-brand-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-deep-purple"
          >
            <Plus className="h-4 w-4" />
            New team
          </button>
        </RoleGate>
      </div>

      {isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load teams: {(error as Error).message}
        </div>
      )}

      {!isLoading && teams.length === 0 && (
        <EmptyState
          icon={Users2}
          title="No teams yet"
          description="Create a team like Nursing, Physio, or Occupational Therapy so you can target broadcasts and reports."
        />
      )}

      {teams.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {teams.map((t) => (
            <TeamCard key={t.id} team={t} />
          ))}
        </div>
      )}

      {isCreating && (
        <TeamForm
          mode="create"
          onClose={() => setIsCreating(false)}
          onSaved={() => setIsCreating(false)}
        />
      )}
    </>
  );
}

function TeamCard({ team }: { team: TeamDto }) {
  return (
    <Link
      to={`/teams/${team.id}`}
      className="flex flex-col gap-2 rounded-lg bg-white p-4 shadow-panel transition hover:shadow-md"
    >
      <div className="flex items-center gap-2">
        <TeamDot colour={team.colour} />
        <div className="font-semibold text-ink">{team.name}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-faint">
        {team.discipline ? (
          <span className="rounded-full bg-surface-3 px-2 py-0.5 capitalize text-ink-dim">
            {team.discipline}
          </span>
        ) : (
          <span className="text-ink-mute">No discipline</span>
        )}
        <span>·</span>
        <span>
          {team.member_count} {team.member_count === 1 ? 'member' : 'members'}
        </span>
      </div>
      {team.description && (
        <p className="line-clamp-2 text-sm text-ink-faint">{team.description}</p>
      )}
    </Link>
  );
}

function TeamDot({ colour }: { colour: string | null }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 rounded-full ring-2 ring-offset-1"
      style={{
        backgroundColor: colour ?? '#4C12A1',
        // @ts-expect-error - ring color via CSS var; Tailwind's ring can't pick up dynamic
        '--tw-ring-color': `${colour ?? '#4C12A1'}33`,
      }}
    />
  );
}

function MemberRow({
  member,
  teamId,
  canRemove,
}: {
  member: TeamMemberDto;
  teamId: string;
  canRemove: boolean;
}) {
  const remove = useRemoveTeamMember(teamId);
  const setRole = useSetTeamMemberRole(teamId);
  const isLeader = member.role === 'leader';
  return (
    <tr className="hover:bg-surface-3">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar name={member.display_name} url={member.avatar_url} />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-ink">{member.display_name}</span>
              {isLeader && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-brand-purple/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand-purple"
                  title="Team leader — has reporting access for this team in the dashboard"
                >
                  <Crown className="h-2.5 w-2.5" />
                  Leader
                </span>
              )}
            </div>
            <div className="text-xs text-ink-faint">{member.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-ink-faint capitalize">{member.app_role}</td>
      <td className="px-4 py-3 text-xs text-ink-faint">
        Joined {formatRelative(member.joined_at)}
      </td>
      {canRemove && (
        <td className="px-4 py-3 text-right">
          <RoleGate allow={['admin']} fallback={null}>
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                disabled={setRole.isPending}
                onClick={() =>
                  setRole.mutate({
                    userId: member.user_id,
                    role: isLeader ? 'member' : 'leader',
                  })
                }
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-ink-dim hover:bg-surface-3 disabled:opacity-40"
                title={isLeader ? 'Demote to member' : 'Promote to team leader'}
              >
                <Crown className="h-3.5 w-3.5" />
                {isLeader ? 'Demote' : 'Make leader'}
              </button>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Remove ${member.display_name} from this team?`)) {
                    remove.mutate(member.user_id);
                  }
                }}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </button>
            </div>
          </RoleGate>
        </td>
      )}
    </tr>
  );
}

function TeamForm({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial?: TeamDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<CreateTeamInput>({
    name: initial?.name ?? '',
    slug: initial?.slug,
    description: initial?.description ?? '',
    discipline: initial?.discipline ?? '',
    colour: initial?.colour ?? '#4C12A1',
  });
  const create = useCreateTeam();
  const update = useUpdateTeam(initial?.id ?? '');
  const mutation = mode === 'create' ? create : update;
  const error = mutation.error as Error | null;

  return (
    <Modal title={mode === 'create' ? 'New team' : `Edit ${initial?.name}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate(
            {
              name: form.name,
              // Only send slug if user touched it or created new (to accept backend default).
              slug: form.slug || undefined,
              description: form.description?.toString().trim() || null,
              discipline: form.discipline?.toString().trim() || null,
              colour: form.colour || null,
            },
            { onSuccess: () => onSaved() },
          );
        }}
        className="flex flex-col gap-4"
      >
        <Field label="Name" required>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Nursing"
            required
            maxLength={80}
            className="w-full rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Discipline" hint="Free text, e.g. nursing, physio, ot">
            <input
              type="text"
              value={form.discipline ?? ''}
              onChange={(e) => setForm({ ...form, discipline: e.target.value })}
              maxLength={80}
              className="w-full rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </Field>
          <Field label="Colour">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.colour ?? '#4C12A1'}
                onChange={(e) => setForm({ ...form, colour: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border border-line-strong"
              />
              <input
                type="text"
                value={form.colour ?? ''}
                onChange={(e) => setForm({ ...form, colour: e.target.value })}
                maxLength={20}
                placeholder="#4C12A1"
                className="flex-1 rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
              />
            </div>
          </Field>
        </div>
        <Field label="Description">
          <textarea
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            maxLength={500}
            rows={3}
            className="w-full rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
          />
        </Field>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            {error.message}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || !form.name.trim()}
            className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : mode === 'create' ? 'Create team' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AssignMembersModal({
  teamId,
  candidates,
  onClose,
}: {
  teamId: string;
  candidates: RosterMember[];
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const add = useAddTeamMembers(teamId);

  const shown = useMemo(() => {
    const f = filter.toLowerCase();
    return candidates.filter(
      (c) => !f || c.display_name.toLowerCase().includes(f) || c.email.toLowerCase().includes(f),
    );
  }, [candidates, filter]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Modal title="Add members" onClose={onClose}>
      <input
        type="search"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by name or email"
        className="w-full rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
      />
      <div className="mt-3 max-h-80 overflow-y-auto rounded-md border border-line">
        {shown.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-ink-faint">
            {candidates.length === 0 ? 'Everyone is already on this team.' : 'No matches.'}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {shown.map((m) => (
              <li key={m.user_id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-surface-3">
                  <input
                    type="checkbox"
                    checked={selected.has(m.user_id)}
                    onChange={() => toggle(m.user_id)}
                    className="h-4 w-4 rounded border-line-strong text-brand-purple focus:ring-brand-purple"
                  />
                  <Avatar name={m.display_name} url={m.avatar_url} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-ink">{m.display_name}</div>
                    <div className="truncate text-xs text-ink-faint">{m.email}</div>
                  </div>
                  <span className="text-xs text-ink-mute capitalize">{m.app_role}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {add.error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {(add.error as Error).message}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between border-t border-line pt-3">
        <span className="text-xs text-ink-faint">{selected.size} selected</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={selected.size === 0 || add.isPending}
            onClick={() =>
              add.mutate([...selected], {
                onSuccess: () => onClose(),
              })
            }
            className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
          >
            {add.isPending ? 'Adding…' : `Add ${selected.size || ''}`.trim()}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DeleteTeamButton({ team, onDeleted }: { team: TeamDto; onDeleted: () => void }) {
  const del = useDeleteTeam();
  return (
    <button
      type="button"
      disabled={del.isPending}
      onClick={() => {
        if (
          window.confirm(
            `Delete team "${team.name}"? This removes all ${team.member_count} membership(s) but does not delete any users.`,
          )
        ) {
          del.mutate(team.id, { onSuccess: onDeleted });
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-40"
    >
      <Trash2 className="h-4 w-4" />
      Delete
    </button>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="modal-title" className="text-base font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-mute hover:bg-surface-3 hover:text-ink-dim"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink-dim">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="text-[11px] text-ink-faint">{hint}</span>}
    </label>
  );
}

function Avatar({ name, url }: { name: string; url: string | null }) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="h-8 w-8 rounded-full object-cover ring-1 ring-gray-200"
      />
    );
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-purple/10 text-xs font-medium text-brand-purple ring-1 ring-brand-purple/20">
      {initials || '?'}
    </div>
  );
}

// Marker export so the module is referenced when tree-shaken.
void cn;
